import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { PanelsTopLeft, User, LogOut, LayoutTemplate, FileText } from 'lucide-react';
import { useAuthStore } from '../store/auth.ts';
import { ThemeToggle } from './ThemeToggle.tsx';

const activeCls = 'bg-primary/10 text-primary';
const idleCls = 'text-foreground/80 hover:bg-muted';
const linkBase = 'flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors';

function NavContents({ onNavigate }: { onNavigate?: () => void }): JSX.Element {
  const logout = useAuthStore((s) => s.logout);
  return (
    <>
      <Link
        to="/"
        onClick={onNavigate}
        activeOptions={{ exact: true }}
        activeProps={{ className: `${linkBase} ${activeCls}` }}
        inactiveProps={{ className: `${linkBase} ${idleCls}` }}
      >
        <LayoutTemplate size={16} /> Builder
      </Link>
      <Link
        to="/content"
        onClick={onNavigate}
        activeProps={{ className: `${linkBase} ${activeCls}` }}
        inactiveProps={{ className: `${linkBase} ${idleCls}` }}
      >
        <FileText size={16} /> Content
      </Link>
      <Link
        to="/profile"
        onClick={onNavigate}
        activeProps={{ className: `${linkBase} ${activeCls}` }}
        inactiveProps={{ className: `${linkBase} ${idleCls}` }}
      >
        <User size={16} /> Profile
      </Link>
      <button type="button" onClick={() => logout()} className={`${linkBase} ${idleCls} w-full text-start`}>
        <LogOut size={16} /> Log out
      </button>
    </>
  );
}

export function AppLayout({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="lg:flex lg:items-start">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:sticky lg:top-0 lg:h-screen w-64 shrink-0 border-e border-border bg-paper/60 px-3 py-4">
        <div className="flex items-center justify-between px-2 pb-4">
          <span className="inline-flex items-center gap-2 font-serif text-xl text-ink">
            <PanelsTopLeft size={18} className="text-primary" /> WapSite
          </span>
          <ThemeToggle />
        </div>
        <nav className="flex flex-col gap-0.5">
          <NavContents />
        </nav>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center gap-2 border-b border-border bg-paper/90 px-4 py-3 backdrop-blur">
        <details className="group relative">
          <summary className="list-none inline-flex items-center gap-2 font-serif text-lg text-ink cursor-pointer">
            <PanelsTopLeft size={18} className="text-primary" /> WapSite
          </summary>
          <nav className="absolute start-0 top-full mt-2 w-64 rounded-xl border border-border bg-paper p-2 shadow-lg flex flex-col gap-0.5 z-40">
            <NavContents />
          </nav>
        </details>
        <div className="ms-auto">
          <ThemeToggle />
        </div>
      </header>

      <div className="min-w-0 flex-1">
        {children}
      </div>
    </div>
  );
}
